import React, { useState, useEffect } from 'react';
import api from '../config/api';

function EditInventoryItemTypesModal({ inventory, onClose, onUpdate }) {
  const [allItemTypes, setAllItemTypes] = useState([]);
  const [selectedItemTypes, setSelectedItemTypes] = useState([]);
  const [defaultItemTypeId, setDefaultItemTypeId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchItemTypes();
    initializeSelections();
  }, [inventory]);

  const fetchItemTypes = async () => {
    try {
      const response = await api.get('/item-types');
      setAllItemTypes(response.data);
    } catch (error) {
      console.error('Error fetching item types:', error);
      alert('Failed to load item types');
    } finally {
      setLoading(false);
    }
  };

  const initializeSelections = () => {
    if (inventory.item_types) {
      const selectedIds = inventory.item_types.map(it => it.id);
      const defaultType = inventory.item_types.find(it => it.is_default);
      
      setSelectedItemTypes(selectedIds);
      setDefaultItemTypeId(defaultType ? defaultType.id : selectedIds[0] || '');
    }
  };

  const handleItemTypeToggle = (itemTypeId) => {
    if (selectedItemTypes.includes(itemTypeId)) {
      const newSelected = selectedItemTypes.filter(id => id !== itemTypeId);
      setSelectedItemTypes(newSelected);
      
      // If we removed the default, set a new default
      if (defaultItemTypeId === itemTypeId) {
        setDefaultItemTypeId(newSelected.length > 0 ? newSelected[0] : '');
      }
    } else {
      const newSelected = [...selectedItemTypes, itemTypeId];
      setSelectedItemTypes(newSelected);
      
      // If this is the first selected, make it default
      if (newSelected.length === 1) {
        setDefaultItemTypeId(itemTypeId);
      }
    }
  };

  const handleDefaultChange = (itemTypeId) => {
    setDefaultItemTypeId(itemTypeId);
  };

  const handleSave = async () => {
    if (selectedItemTypes.length === 0) {
      alert('Please select at least one item type');
      return;
    }

    if (!defaultItemTypeId || !selectedItemTypes.includes(defaultItemTypeId)) {
      alert('Please select a default item type from the selected types');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(`/inventories/${inventory.id}/item-types`, {
        item_types: selectedItemTypes,
        default_item_type_id: defaultItemTypeId
      });

      alert('Item types updated successfully!');
      onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error('Error updating item types:', error);
      alert('Failed to update item types: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const getItemTypeById = (id) => {
    return allItemTypes.find(type => type.id === parseInt(id));
  };

  const defaultItemType = getItemTypeById(defaultItemTypeId);

  if (loading) {
    return (
      <div className="modal">
        <div className="modal-content">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="modal">
      <div className="modal-content" style={{ maxWidth: '600px' }}>
        <h2>Edit Item Types for "{inventory.name}"</h2>
        
        <div className="form-group">
          <label>Current Item Types</label>
          <div style={{ marginBottom: '1rem' }}>
            {inventory.item_types && inventory.item_types.length > 0 ? (
              inventory.item_types.map(itemType => (
                <span 
                  key={itemType.id}
                  style={{
                    display: 'inline-block',
                    backgroundColor: itemType.is_default ? '#e8f4f8' : '#f5f5f5',
                    border: itemType.is_default ? '1px solid #0066cc' : '1px solid #ddd',
                    color: itemType.is_default ? '#0066cc' : '#333',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    marginRight: '0.5rem',
                    marginBottom: '0.25rem'
                  }}
                >
                  {itemType.name}
                  {itemType.is_default && ' (Default)'}
                </span>
              ))
            ) : (
              <span style={{ color: '#888' }}>No item types currently assigned</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Select Item Types</label>
          <div style={{ 
            border: '1px solid #ddd', 
            borderRadius: '4px', 
            maxHeight: '300px', 
            overflowY: 'auto',
            padding: '0.5rem'
          }}>
            {allItemTypes.map(itemType => (
              <div key={itemType.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                padding: '0.5rem',
                borderBottom: '1px solid #eee'
              }}>
                <input
                  type="checkbox"
                  id={`itemtype-${itemType.id}`}
                  checked={selectedItemTypes.includes(itemType.id)}
                  onChange={() => handleItemTypeToggle(itemType.id)}
                />
                <label 
                  htmlFor={`itemtype-${itemType.id}`}
                  style={{ 
                    flex: 1, 
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <strong>{itemType.name}</strong>
                    {itemType.description && (
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {itemType.description}
                      </div>
                    )}
                    <div style={{ fontSize: '0.8rem', color: '#888' }}>
                      {itemType.fields?.length || 0} fields
                    </div>
                  </div>
                  {selectedItemTypes.includes(itemType.id) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        name="defaultItemType"
                        id={`default-${itemType.id}`}
                        checked={defaultItemTypeId === itemType.id}
                        onChange={() => handleDefaultChange(itemType.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <label 
                        htmlFor={`default-${itemType.id}`}
                        style={{ fontSize: '0.8rem', color: '#0066cc', cursor: 'pointer' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Default
                      </label>
                    </div>
                  )}
                </label>
              </div>
            ))}
          </div>
          
          {selectedItemTypes.length === 0 && (
            <div style={{ fontSize: '0.8rem', color: '#e74c3c', marginTop: '0.5rem' }}>
              Please select at least one item type
            </div>
          )}
          {selectedItemTypes.length > 0 && (
            <div style={{ fontSize: '0.8rem', color: '#27ae60', marginTop: '0.5rem' }}>
              {selectedItemTypes.length} item type{selectedItemTypes.length > 1 ? 's' : ''} selected
              {defaultItemType && ` • Default: ${defaultItemType.name}`}
            </div>
          )}
        </div>

        {defaultItemType && (
          <div className="form-group">
            <label>Default Item Type Preview</label>
            <div style={{ border: '1px solid #ddd', padding: '1rem', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
              <p><strong>{defaultItemType.name}</strong> <span style={{ fontSize: '0.8rem', color: '#0066cc' }}>(Default)</span></p>
              {defaultItemType.description && <p style={{ fontSize: '0.9rem', color: '#666' }}>{defaultItemType.description}</p>}
              {defaultItemType.fields && defaultItemType.fields.length > 0 ? (
                <div>
                  <p style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>New items will use these fields by default:</p>
                  {defaultItemType.fields.slice(0, 3).map((field, index) => (
                    <div key={index} style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      • <strong>{field.name}</strong> ({field.type})
                      {field.required && <span style={{ color: '#e74c3c' }}> *</span>}
                    </div>
                  ))}
                  {defaultItemType.fields.length > 3 && (
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                      +{defaultItemType.fields.length - 3} more fields
                    </div>
                  )}
                </div>
              ) : (
                <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>No fields defined for this item type.</p>
              )}
            </div>
          </div>
        )}

        <div style={{ 
          backgroundColor: '#fff3cd', 
          padding: '1rem', 
          borderRadius: '4px', 
          border: '1px solid #ffeaa7',
          margin: '1rem 0',
          fontSize: '0.9rem'
        }}>
          <strong>⚠️ Important:</strong>
          <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
            <li>Changing item types will affect how new items are created</li>
            <li>Existing items will retain their current item type associations</li>
            <li>The default item type is used when creating new items</li>
          </ul>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            type="button" 
            className="btn" 
            onClick={handleSave}
            disabled={selectedItemTypes.length === 0 || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditInventoryItemTypesModal;